import { Close, Description, FolderOpen } from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useMemo, useRef, useState } from 'react';

import { draftTimelogsApi } from '@/api/draftTimelogsApi';
import { timelogsApi } from '@/api/timelogsApi';
import { yandexTrackerApi } from '@/api/yandexTrackerApi';
import { Input } from '@/components/Input';
import { InputWrapper } from '@/components/InputWrapper/InputWrapper';
import { LogsMode, useLogsMode } from '@/features/logsMode';
import { SuggestionsModel } from '@/models/SuggestionsModel';
import { TrackerQueue } from '@/types';
import { YandexTask } from '@/types/yandex';
import { transformTimelogsTask } from '@/utils/transformTimelog';

type SelectorValue = {
  taskKey?: string | null;
  taskTitle?: string | null;
  queueKey?: string | null;
  queueName?: string | null;
};

type TaskQueueSelectorProps = {
  value: SelectorValue;
  onChange: (value: SelectorValue) => void;
  onClear: () => void;
  autoFocus?: boolean;
};

const deriveLabel = (value: SelectorValue): string => {
  if (value.taskKey) {
    return `${value.taskKey} — ${value.taskTitle ?? ''}`.trim();
  }

  if (value.queueKey) {
    return `${value.queueKey} — ${value.queueName ?? ''}`.trim();
  }

  return '';
};

const extractQueueFromTaskKey = (taskKey: string): string => {
  return taskKey.split('-')[0] ?? taskKey;
};

export const TaskQueueSelector = observer(
  ({ value, onChange, onClear, autoFocus }: TaskQueueSelectorProps) => {
    const [query, setQuery] = useState(deriveLabel(value));
    const [showSuggestions, setShowSuggestions] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const mode = useLogsMode();

    const taskSuggestionsModel = useMemo(
      () =>
        new SuggestionsModel<YandexTask>({
          fetchFn: async ({ query: q, signal }) => {
            if (!q.trim()) return [];

            if (mode === LogsMode.v2) {
              const tasks = await timelogsApi.suggestTasks({ query: q, signal });

              return tasks.map(transformTimelogsTask);
            }

            return yandexTrackerApi.searchAllTasks(q, signal);
          },
        }),
      [mode],
    );

    const queueSuggestionsModel = useMemo(
      () =>
        new SuggestionsModel<TrackerQueue>({
          fetchFn: async ({ query: q, signal }) => {
            if (!q.trim()) return [];

            return draftTimelogsApi.suggestQueues(q, signal);
          },
        }),
      [],
    );

    const taskSuggestions = taskSuggestionsModel.suggestions;
    const queueSuggestions = queueSuggestionsModel.suggestions;

    const hasSuggestions = taskSuggestions.length > 0 || queueSuggestions.length > 0;

    const isOpen = showSuggestions && !!query.trim();

    return (
      <InputWrapper label="Задача/очередь">
        <Box ref={anchorRef}>
          <Input
            value={query}
            autoFocus={autoFocus}
            onChange={(e) => {
              const nextValue = e.target.value;
              setQuery(nextValue);
              setShowSuggestions(true);
              taskSuggestionsModel.setSuggestQuery(nextValue);
              queueSuggestionsModel.setSuggestQuery(nextValue);

              if (!nextValue.trim()) {
                onClear();
              }
            }}
            onFocus={() => {
              setShowSuggestions(true);

              if (query.trim()) {
                taskSuggestionsModel.setSuggestQuery(query);
                queueSuggestionsModel.setSuggestQuery(query);
              }
            }}
            placeholder="Введите задачу или очередь"
            InputProps={{
              endAdornment: query ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    setQuery('');
                    onClear();
                    setShowSuggestions(false);
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              ) : undefined,
            }}
          />
        </Box>

        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1500, width: anchorRef.current?.offsetWidth }}
          disablePortal={false}
        >
          <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
            {hasSuggestions ? (
              <Paper
                sx={{
                  mt: 0.5,
                  maxHeight: 300,
                  overflow: 'auto',
                  boxShadow: 3,
                  fontFamily: 'Montserrat, sans-serif',
                  '& .MuiMenuItem-root': { fontFamily: 'inherit' },
                  '& .MuiListSubheader-root': { fontFamily: 'inherit' },
                  '& .MuiTypography-root': { fontFamily: 'inherit' },
                }}
              >
                <MenuList dense>
                  {queueSuggestions.length > 0 && (
                    <>
                      <ListSubheader sx={{ lineHeight: '32px' }}>Очереди</ListSubheader>
                      {queueSuggestions.map((queue) => (
                        <MenuItem
                          key={queue.key}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            const nextValue: SelectorValue = {
                              taskKey: null,
                              taskTitle: null,
                              queueKey: queue.key,
                              queueName: queue.name,
                            };
                            onChange(nextValue);
                            setQuery(`${queue.key} — ${queue.name}`);
                            setShowSuggestions(false);
                          }}
                        >
                          <ListItemIcon>
                            <FolderOpen fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>
                            <Typography component="span" fontWeight={500}>
                              {queue.key}
                            </Typography>{' '}
                            <Typography component="span" color="text.secondary">
                              {queue.name}
                            </Typography>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </>
                  )}

                  {taskSuggestions.length > 0 && (
                    <>
                      <ListSubheader sx={{ lineHeight: '32px' }}>Задачи</ListSubheader>
                      {taskSuggestions.map((task) => (
                        <MenuItem
                          key={task.key}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            const nextValue: SelectorValue = {
                              taskKey: task.key,
                              taskTitle: task.summary,
                              queueKey: extractQueueFromTaskKey(task.key),
                              queueName: value.queueName ?? null,
                            };
                            onChange(nextValue);
                            setQuery(`${task.key} — ${task.summary}`);
                            setShowSuggestions(false);
                          }}
                        >
                          <ListItemIcon>
                            <Description fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>
                            <Typography component="span" fontWeight={500}>
                              {task.key}
                            </Typography>{' '}
                            <Typography component="span" color="text.secondary">
                              {task.summary}
                            </Typography>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </>
                  )}
                </MenuList>
              </Paper>
            ) : (
              <Paper
                sx={{
                  mt: 0.5,
                  boxShadow: 3,
                  p: 2,
                  fontFamily: 'Montserrat, sans-serif',
                  '& .MuiTypography-root': { fontFamily: 'inherit' },
                }}
              >
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Ничего не найдено
                </Typography>
              </Paper>
            )}
          </ClickAwayListener>
        </Popper>
      </InputWrapper>
    );
  },
);
